import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspaceMode } from "@/hooks/useWorkspaceMode";
import { supabase } from "@/integrations/supabase/client";
import { WelcomeWizard } from "./WelcomeWizard";
import { BusinessWelcomeWizard } from "./BusinessWelcomeWizard";

type ProfileRow = {
  first_name: string;
  role: string;
  is_business_owner: boolean;
  district: string | null;
  avatar_url: string | null;
  open_to_meet: boolean;
  children_info: any;
  onboarding_completed_at: string | null;
  business_onboarding_completed_at: string | null;
  business_avatar_url: string | null;
  business_bio: string | null;
  website: string | null;
  instagram_handle: string | null;
};

/**
 * Member welcome flow for parents/carers; business setup flow for business-only
 * accounts, and for dual-role accounts the first time they're in business view.
 */
export const OnboardingTourGate = () => {
  const { user, loading } = useAuth();
  const { mode } = useWorkspaceMode();
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [checkedFor, setCheckedFor] = useState<string | null>(null);
  const [memberDone, setMemberDone] = useState(false);
  const [businessDone, setBusinessDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (loading) return;
    if (!user) { setProfile(null); setCheckedFor(null); return; }
    if (checkedFor === user.id) return;
    if (!user.email_confirmed_at && !user.confirmed_at) return;

    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, role, is_business_owner, district, avatar_url, open_to_meet, children_info, onboarding_completed_at, business_onboarding_completed_at, business_avatar_url, business_bio, website, instagram_handle")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setCheckedFor(user.id);
      if (error || !data) return;
      const row = data as unknown as ProfileRow;
      setMemberDone(!!row.onboarding_completed_at);
      setBusinessDone(!!row.business_onboarding_completed_at);
      setProfile(row);
    })();
    return () => { cancelled = true; };
  }, [user, loading, checkedFor]);

  if (!user) return null;

  // While the profile check is in flight for a verified user, cover the page
  // with an opaque loader so the homepage never flashes before onboarding.
  if (!profile) {
    if (checkedFor === user.id) return null; // check finished, no profile row
    if (!user.email_confirmed_at && !user.confirmed_at) return null; // unverified users aren't checked
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  const isBusinessOnly = profile.role === "other";
  const isDual = !isBusinessOnly && profile.is_business_owner;

  if (isBusinessOnly && !businessDone) {
    return <BusinessWelcomeWizard userId={user.id} firstName={profile.first_name} initial={profile} onDone={() => { setBusinessDone(true); setMemberDone(true); }} />;
  }
  if (!isBusinessOnly && !memberDone) {
    return <WelcomeWizard userId={user.id} firstName={profile.first_name} initial={profile} isDual={isDual} onDone={() => setMemberDone(true)} />;
  }
  if (isDual && !businessDone && mode === "business") {
    return <BusinessWelcomeWizard short userId={user.id} firstName={profile.first_name} initial={profile} onDone={() => setBusinessDone(true)} />;
  }
  return null;
};

export default OnboardingTourGate;
